import { CaretRight } from "@phosphor-icons/react"
import { useQuery } from "@tanstack/react-query"
import { BaseButton, Button } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Field } from "@tokenterminal/core/ui/input/Field"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { Skeleton } from "@tokenterminal/core/ui/progress/skeleton/Skeleton"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { trpc } from "@tokenterminal/core/utils/trpc"
import { type ProjectDetailsMeta } from "@tokenterminal/tt-types"
import { calculateTimeAgoValue } from "@tokenterminal/ui/utils/date"
import { getLatestPriceLabel } from "@tokenterminal/ui/utils/numerals"
import { ErrorBoundary } from "components/ErrorBoundary"
import { apiGET } from "data/api/get"
import { useProjectPriceV3 } from "hooks/useProjectPrice"
import { ProjectSocialMediaLinks } from "modules/v3/ProjectHero/ProjectSocialMediaLinks"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { Fragment, useMemo, useState } from "react"
import { getProjectPageRoute } from "utils/getProjectPageRoute"
import { type RouterOutputs, useTRPC } from "utils/trpc"
import { defaultMetricSortingConfig } from "../../../utils/metricSortingConfig"
import useKeyMetrics from "../hooks/useKeyMetrics"
import { useMetricsConfigurationList } from "../hooks/useMetricsConfigurationList"
import { useProjectsList } from "../hooks/useProjectsList"
import { useTimeseries } from "../hooks/useTimeseries"
import useShouldShowEcosystemTab from "../useShouldShowEcosystemTab"
import { ComparisonChartWithMetricDropdown } from "./ComparisonChartWithMetricDropdown"
import OverviewModalContent from "./OverviewModalContent"
import { Products } from "./Products"

const DashboardsList = ({
  name,
  projectApiId,
}: {
  name: string
  projectApiId: string
}) => {
  const { data: dashboards } = useQuery({
    queryKey: ["dashboards", projectApiId],
    queryFn: () =>
      apiGET(
        `/v1/dashboards?project_data_ids[0]=${projectApiId}` as "/v1/dashboards"
      ),
  })

  const relatedDashboards = dashboards?.data || []
  if (relatedDashboards.length === 0) {
    return null
  }

  return (
    <div className="flex-1 max-w-none lg:max-w-[50%]">
      <Heading level={4} className="mb-3">
        Analyst coverage
      </Heading>
      <div className="flex flex-col gap-px">
        {relatedDashboards.slice(0, 4).map((dashboard) => (
          <Link
            key={dashboard.user_dashboard_id}
            href={`/studio/dashboards/${dashboard.user_dashboard_id}`}
            className="p-2 flex gap-3 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 rounded-lg"
          >
            <Image
              src={dashboard.logo_url ?? ""}
              alt={dashboard.name ?? ""}
              width={24}
              height={24}
              className="w-5 h-5 m-1 rounded"
            />
            <div className="flex flex-col text-sm">
              <div className="text-fg-default font-medium">
                {dashboard.name}
              </div>
              <div className="text-fg-secondary">{dashboard.description}</div>
            </div>
          </Link>
        ))}
        {relatedDashboards.length > 4 && (
          <div className="flex justify-start my-4 ml-12">
            <Link
              href={`/discover?search=${encodeURIComponent(`"${name}"`)}`}
              className="text-blue-700 dark:text-blue-400 text-sm flex items-center gap-1 hover:underline"
            >
              Explore all related coverage
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

function getDateAsNumber(dateString: string) {
  return new Date(dateString).getTime()
}

// Add more interesting loading state, possibly fallback to previous data
const NewsArticlesList = ({ projectSlug }: { projectSlug: string }) => {
  const trpc = useTRPC()

  const {
    data: { articles: relatedArticles = [] } = { articles: [] },
    isLoading,
  } = useQuery(
    trpc.projects.getNews.queryOptions({
      project_slug: projectSlug,
    })
  )

  return (
    <div className="flex-1 max-w-none lg:max-w-[50%]">
      <Heading level={4} className="mb-3">
        In the news
      </Heading>
      {isLoading && (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="p-2 flex gap-4 rounded-lg animate-pulse bg-neutral-950/5 dark:bg-neutral-50/5"
            >
              <div className="flex flex-col gap-2 w-full">
                <div className="h-2 w-2/4 bg-neutral-200 dark:bg-neutral-700 rounded" />
                <div className="h-3 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded" />
                <div className="h-2 w-1/4 bg-neutral-200 dark:bg-neutral-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
      {!isLoading && !relatedArticles?.length && (
        <div className="">
          <div className="text-fg-secondary text-sm">
            No recent news articles found for this project.
          </div>
        </div>
      )}
      <div className="flex flex-col gap-px">
        {!isLoading &&
          relatedArticles?.slice(0, 4).map((article) => (
            <a
              key={article.title}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 flex gap-3 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 rounded-lg"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={
                  article.link
                    ? `https://www.google.com/s2/favicons?domain=${new URL(article.link).hostname}&sz=32`
                    : ""
                }
                alt={article.source}
                className="w-5 h-5 m-1 rounded"
              />
              <div className="flex flex-col text-sm">
                <div className="text-fg-default font-medium">
                  {article.title}
                </div>
                <div className="text-fg-secondary text-sm flex items-center gap-1.5">
                  <span>{article.source}</span>
                  <span>•</span>
                  <span>
                    {calculateTimeAgoValue(getDateAsNumber(article.date))} ago
                  </span>
                </div>
              </div>
            </a>
          ))}
      </div>
    </div>
  )
}

const DeployedOnField = ({ data }: { data: ProjectDetailsMeta }) => {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const { data: { data: chainsMetadata = [] } = {} } = useQuery(
    trpc.chains.getChains.queryOptions()
  )
  const chainsMetadataDict = useMemo(
    () => toDictionary(chainsMetadata, "chain_id"),
    [chainsMetadata]
  )

  const { data: timeseriesData } = useTimeseries({
    dataIds: [data.data_id],
    metricIds: ["active_addresses_daily"],
    interval: "7d",
    groupBy: "chain",
  })

  const latestTimeseriesData = useMemo(() => {
    if (!timeseriesData?.data) return {}

    // Group by chain_id
    const groupedByChain = timeseriesData.data
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .reduce(
        (acc, item) => {
          if (item.chain_id && !acc[item.chain_id]) {
            acc[item.chain_id] = {
              chain_id: item.chain_id,
              value: item.value,
              timestamp: item.timestamp,
            }
          }
          return acc
        },
        {} as Record<
          string,
          { chain_id: string; value: number | null; timestamp: string }
        >
      )

    // Get latest entry for each chain
    return groupedByChain
  }, [timeseriesData])

  type ChainItem = {
    logo: string | null
    name: string
    slug: string
    chain_id: string
    value: number | null
  }

  const chainItems = useMemo(() => {
    const chainIds = new Set(
      timeseriesData?.sorted_data_ids
        .map(({ chain_id }) => chain_id)
        .filter(Boolean)
    ) as Set<string>

    data.chains.forEach((chain) => {
      chainIds.add(chain)
    })

    const filteredChainIds = Array.from(chainIds).filter(
      (chain_id) => chain_id !== data.data_id
    )

    return (filteredChainIds
      .map((chain_id) => {
        const chain = chainsMetadataDict.get(chain_id!)

        if (!chain) {
          return null
        }

        return {
          logo: chain?.logo,
          name: chain?.name || chain_id,
          slug: chain?.project_slug,
          chain_id,
          value: latestTimeseriesData[chain_id!]?.value || null,
        }
      })
      .filter(Boolean) || []) as ChainItem[]
  }, [
    chainsMetadataDict,
    data.chains,
    data.data_id,
    latestTimeseriesData,
    timeseriesData?.sorted_data_ids,
  ])

  if (chainItems?.length === 0) {
    return null
  }

  return (
    <Field label="Deployed on">
      <Button
        className="justify-between"
        onPress={() => {
          if (chainItems?.length === 1) {
            router.push(`/projects/${chainItems[0].slug}`)
          } else {
            setIsOpen(true)
          }
        }}
      >
        <div className="flex gap-4">
          <div className="flex items-center">
            {chainItems
              ?.slice(0, 3)
              .map((chain) => (
                <Avatar
                  key={chain.name}
                  src={`https://static1.tokenterminal.com/${chain.logo}`}
                  className="w-4 h-4 rounded-full overflow-hidden -mr-2"
                />
              ))}
          </div>
          {chainItems?.length === 1
            ? chainItems[0].name
            : `${chainItems?.length} chains`}
        </div>
        <Icon as={CaretRight} />
      </Button>
      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Header
          title={`${data?.name} is deployed on ${chainItems?.length} chain${chainItems?.length === 1 ? "" : "s"}`}
        />
        <Modal.Body>
          <div>
            <div className="flex justify-between mb-2 px-0 text-fg-secondary text-sm">
              <div>Chains</div>
              <div>Active addresses last 7 days</div>
            </div>
            <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
              {chainItems.map((chain) => (
                <Link
                  key={chain.name}
                  href={`/projects/${chain.slug}`}
                  className="flex justify-between items-center gap-2 p-3 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 border-b border-solid border-border-default last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={`https://static1.tokenterminal.com/${chain.logo}`}
                      className="w-4 h-4 rounded-full overflow-hidden"
                    />
                    <span>{chain.name}</span>
                  </div>

                  {chain.value ? (
                    <div className="uppercase">
                      {formatNumber({
                        value: chain.value ?? 0,
                        format: "compact",
                      })}
                    </div>
                  ) : (
                    <div className="">N/A</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-end">
          <Button onPress={() => setIsOpen(false)}>Done</Button>
        </Modal.Footer>
      </Modal>
    </Field>
  )
}

const IssuerOfField = ({ data }: { data: ProjectDetailsMeta }) => {
  const issuerLookup = {
    circle: "USDC",
    agora: "AUSD",
    tether: "USDT",
    ethena: "USDe",
  }

  const stablecoin = issuerLookup[data.api_id as keyof typeof issuerLookup]

  if (!stablecoin) {
    return null
  }

  return (
    <Field label="Issuer of">
      <Button className="justify-between pointer-events-none">
        <div className="flex gap-4">
          <div className="flex items-center">
            <Avatar
              src={`https://static1.tokenterminal.com/${data.logo}`}
              className="w-4 h-4 rounded-full overflow-hidden -mr-2"
            />
          </div>
          {stablecoin}
        </div>
        {/* <Icon as={CaretRight} /> */}
      </Button>
    </Field>
  )
}

const RelatedProjects = ({ data }: { data: ProjectDetailsMeta }) => {
  const count = 4
  const { data: projectsList = [] } = useProjectsList({
    marketSectors: [data.market_sectors[0].id],
  })

  if (projectsList.length === 0) {
    return null
  }

  const indexOfProject = projectsList.findIndex(
    (project) => project.data_id === data.api_id
  )
  let startIndex
  if (indexOfProject >= projectsList.length - 4) {
    const distanceFromEnd = projectsList.length - 4 - indexOfProject
    startIndex = Math.max(indexOfProject - (count - distanceFromEnd) / 2, 0)
  } else {
    startIndex = Math.max(indexOfProject - count / 2, 0)
  }
  const endIndex = Math.min(startIndex + count, projectsList.length)

  const projects = projectsList
    .filter((project) => project.data_id !== data.api_id)
    .slice(startIndex, endIndex)

  return (
    <Field label="Similar projects">
      <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
        {projects.map(
          (project: { name: string; slug: string; logo: string }) => (
            <Link
              key={project.name}
              href={`/projects/${project.slug}`}
              className="flex items-center gap-2 p-2 text-sm hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 border-b border-solid border-border-default last:border-b-0"
            >
              <Avatar
                src={`https://static1.tokenterminal.com/${project.logo}`}
                className="w-4 h-4 rounded-full overflow-hidden"
              />
              <span>{project.name}</span>
            </Link>
          )
        )}
      </div>
    </Field>
  )
}

const LatestPriceSnippet = ({
  data,
  slug,
}: {
  data: ProjectDetailsMeta
  slug: string
}) => {
  const livePrice = useProjectPriceV3(slug)
  const { accuratePrice, formattedPrice } = getLatestPriceLabel(
    livePrice?.usd ?? data.price_data?.latest.value
  )

  return (
    <div className="flex flex-col gap-1 mb-4">
      <div className="flex items-center gap-2 text-sm text-fg-secondary font-medium">
        Price
        <div className="bg-emerald-500 dark:bg-emerald-400 w-1.5 h-1.5 rounded-full" />
      </div>
      <div
        className={`self-start text-2xl font-medium uppercase ${accuratePrice ? "underline decoration-dotted decoration-1 decoration-neutral-900/[0.2] dark:decoration-neutral-100/[0.2] underline-offset-4" : ""}`}
        style={{ fontFamily: "var(--font-geist-mono)" }}
        data-tooltip={accuratePrice}
      >
        {formattedPrice.replace("~", "")}
      </div>
    </div>
  )
}

const getBadgeColor = (metricChange: number | null) => {
  if (
    metricChange === null ||
    metricChange === 0 ||
    Math.abs(metricChange) < 0.005
  ) {
    return "neutral"
  } else if (metricChange > 0) {
    return "green"
  }
  return "red"
}

const getBadgePreface = (metricChange: number) => {
  if (metricChange === 0) {
    return ""
  } else if (Math.abs(metricChange) < 0.005) {
    return "≈"
  }

  return metricChange > 0 ? "+" : "-"
}

const isNotDeployedOnSelf = (data: ProjectDetailsMeta) => {
  const isL1Chain = data.flattened_tags.some((tag) => tag === "blockchains-l1")
  return (
    (data?.chains?.length !== 1 || data?.chains[0] !== data.api_id) &&
    !isL1Chain
  )
}

type GetProjectProps = {
  projectData: RouterOutputs["projects"]["getProject"]
}

type OverviewTabProjectProps = {
  type: "project"
} & GetProjectProps

type OverviewTabProductProps = {
  type: "product"
  productData: RouterOutputs["products"]["getProducts"]["data"][0]
} & GetProjectProps

export type OverviewTabProps = OverviewTabProjectProps | OverviewTabProductProps

const OverviewTab = (props: OverviewTabProps) => {
  const { type, projectData } = props
  const IS_PRODUCT_VIEW = useMemo(() => type === "product", [type])

  const { data: shouldShowEcosystemTab } = useShouldShowEcosystemTab({
    chainId: projectData.data_id,
  })
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false)
  const { data: metricsConfigurationList = [] } = useMetricsConfigurationList()

  const { data: keyMetrics, isLoading } = useKeyMetrics({
    dataId: projectData.data_id,
    projectSlug: projectData.slug,
    marketSectorId: projectData.market_sectors[0].id,
    productId:
      props.type === "product" ? props.productData.product_id : undefined,
  })

  // Create a dictionary of metric configurations for sorting
  const metricsConfigurationListDic = useMemo(() => {
    return new Map(
      metricsConfigurationList.map((metric) => [metric.metric_id, metric])
    )
  }, [metricsConfigurationList])

  // Sort key metrics based on the defaultMetricSortingConfig
  const sortedKeyMetrics = useMemo(() => {
    if (!keyMetrics || keyMetrics.length === 0) return []

    // Get the category for each metric
    const metricsWithCategory = keyMetrics.map((metric) => {
      const metricConfig = metricsConfigurationListDic.get(metric.metric_id)
      const category = metricConfig?.categories?.[0]?.toLowerCase() || "other"
      return { ...metric, category }
    })

    return metricsWithCategory.sort((a, b) => {
      const categoryA = a.category
      const categoryB = b.category

      // If metrics are in different categories, sort by category order
      if (categoryA !== categoryB) {
        const categoryOrderA =
          defaultMetricSortingConfig.categoryOrder?.indexOf(categoryA) ?? 999
        const categoryOrderB =
          defaultMetricSortingConfig.categoryOrder?.indexOf(categoryB) ?? 999
        return categoryOrderA - categoryOrderB
      }

      // If metrics are in the same category, use the category-specific order
      const metricOrder =
        defaultMetricSortingConfig.categoryMetricOrder?.[categoryA] || []

      if (metricOrder.length > 0) {
        const indexA = metricOrder.indexOf(a.metric_id)
        const indexB = metricOrder.indexOf(b.metric_id)

        // If both metrics are in the custom order, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }

        // If only one metric is in the custom order, prioritize it
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
      }

      // Default to alphabetical sort by title
      return a.label.localeCompare(b.label)
    })
  }, [keyMetrics, metricsConfigurationListDic])

  const shouldShowLearnMore =
    projectData.description_what !== "" ||
    projectData.description_how !== "" ||
    projectData.description_who !== ""

  return (
    <div className="flex flex-col md:flex-row gap-10">
      {/* Left Column */}
      <div className="flex flex-col gap-8 w-full md:w-[320px]">
        {/* Key Metrics */}
        <div className="w-full flex flex-col">
          {!IS_PRODUCT_VIEW && projectData.price_data.latest.value && (
            <div className="mb-2">
              <LatestPriceSnippet data={projectData} slug={projectData.slug} />
            </div>
          )}
          <label className="tt-field-label text-fg-secondary mb-3">
            Key metrics
          </label>
          {isLoading ? (
            <>
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 py-2 border-t border-solid border-border-default last-of-type:border-b"
                >
                  <Skeleton className="h-[20px] w-1/3 group-odd:w-1/4 rounded-md" />
                  <Skeleton className="h-[20px] w-[80px] rounded-md" />
                  <Skeleton className="h-[20px] w-[60px] rounded-md" />
                </div>
              ))}
            </>
          ) : (
            <div className="grid grid-cols-[1fr_auto_auto]">
              {sortedKeyMetrics.map((metric) => {
                const badgeColor = getBadgeColor(metric.change)

                return (
                  <Fragment key={metric.metric_id}>
                    <div className="py-2 pr-2 border-solid border-b border-border-default flex items-center">
                      <Link
                        href={
                          metric.metric_id === "gdp"
                            ? getProjectPageRoute({
                                projectSlug: projectData.slug,
                                ecosystemMetricId: "gdp",
                              })
                            : getProjectPageRoute({
                                projectSlug: projectData.slug,
                                productId:
                                  props.type === "product"
                                    ? props.productData.product_id
                                    : undefined,
                                metricSlug: metric.slug,
                              })
                        }
                        className="text-fg-default text-sm"
                      >
                        {metric.label}
                      </Link>
                    </div>
                    <div className="p-2 border-solid border-b border-border-default flex items-center justify-end">
                      <span className="text-fg-secondary text-sm text-right">
                        {formatNumber({
                          value: metric.value,
                          format: "compact",
                          metricFormat: metric.metricFormat,
                          prefix: metric.prefix, //TODO: update key metrics API to use
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-start py-2 border-solid border-b border-border-default">
                      <Tooltip
                        content="30 day change"
                        tooltipProps={{ placement: "right" }}
                      >
                        <BaseButton className="cursor-default flex">
                          <Badge
                            color={badgeColor}
                            variant="outline"
                            size="small"
                            className={`!py-0 !gap-0 tabular-nums tracking-wider ${badgeColor === "neutral" ? "!text-neutral-600 dark:!text-neutral-400" : ""}`}
                          >
                            <span className="mr-px">
                              {getBadgePreface(metric.change)}
                            </span>
                            {Math.abs(metric.change * 100).toFixed(1)}%
                          </Badge>
                        </BaseButton>
                      </Tooltip>
                    </div>
                  </Fragment>
                )
              })}
            </div>
          )}
          <Link
            href={getProjectPageRoute({
              projectSlug: projectData.slug,
              productId:
                props.type === "product"
                  ? props.productData.product_id
                  : undefined,
              metricSlug: "all",
            })}
            className="self-start text-blue-700 dark:text-blue-400 text-sm mt-3"
          >
            Explore all metrics
          </Link>
        </div>
        {/* Bring this back once we have live data */}
        {/* <IssuerOfField data={data} /> */}
        {!IS_PRODUCT_VIEW && isNotDeployedOnSelf(projectData) && (
          <>
            <DeployedOnField data={projectData} />
            <Products projectSlug={projectData.slug} />
          </>
        )}
        <div className="flex flex-col gap-4">
          {projectData?.description_what && (
            <p className="text-fg-secondary text-sm">
              {projectData?.description_what}
            </p>
          )}
          <ProjectSocialMediaLinks
            name={projectData.name}
            links={projectData.links}
          />
          {shouldShowLearnMore && (
            <button
              onClick={() => setIsAboutModalOpen(true)}
              className="self-start text-blue-700 dark:text-blue-400 text-sm"
            >
              Learn more
            </button>
          )}
        </div>

        {!IS_PRODUCT_VIEW && <RelatedProjects data={projectData} />}
      </div>

      {/* Center Panel - Reserved for chart/content */}
      <div className="flex-1 flex-col flex gap-10">
        <div className="flex-col flex">
          <Card className="rounded-2xl flex flex-col p-6 md:p-8">
            <ErrorBoundary fallback={<div>Error</div>}>
              {sortedKeyMetrics.length > 0 && (
                <ComparisonChartWithMetricDropdown
                  data={projectData}
                  metric1Default={keyMetrics?.[0]?.slug || "fees"}
                  productId={
                    props.type === "product"
                      ? props.productData.product_id
                      : undefined
                  }
                />
              )}
            </ErrorBoundary>
          </Card>
        </div>
        {!IS_PRODUCT_VIEW && (
          <>
            <Divider />
            {/* Bottom row */}
            <div className="flex flex-col lg:flex-row gap-6 w-full">
              <DashboardsList
                name={projectData.name}
                projectApiId={projectData.api_id}
              />
              <ErrorBoundary
                fallback={
                  <div className="flex-1 max-w-none lg:max-w-[50%]">
                    <Heading level={4} className="mb-3">
                      In the news
                    </Heading>
                    <div className="">
                      <div className="text-fg-secondary text-sm">
                        No recent news articles found for this project.
                      </div>
                    </div>
                  </div>
                }
              >
                <NewsArticlesList projectSlug={projectData.slug} />
              </ErrorBoundary>
            </div>
          </>
        )}
      </div>
      <Modal
        isOpen={isAboutModalOpen}
        onOpenChange={setIsAboutModalOpen}
        size="large"
        hasDividers
      >
        <Modal.Header title="About this project" />
        <Modal.Body>
          <OverviewModalContent data={projectData} />
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default OverviewTab
